// Section 12: healers, medicine and triage; Section 16 trust rows (treated when short of medicine
// +6, passed over -8); Section 19 quarantine effects; Section 17 "medicine and healer time use the
// health-care policy".
//
// The plan is a pure function of the committed state at the start of the bodies phase (healing
// slots produced this season, medicine on hand, who is sick, the policy and the player's triage
// overrides), memoised per state. Its outcome per person is written to `report.care`, which is
// what the healer and plague modifiers read: `careContext(ctx)` is the phase-start state with
// that map, so the death roll and health gain see this season's treatment while every
// evaluation still reads one fixed state (ADR-0010 item 20).
import { clamp } from '../../core/math';
import { memoByState } from '../../core/memo';
import {
  contagiousCount,
  isChild,
  isIsolated,
  isPatient,
  needsOf,
  peopleSorted,
  quarantineActive,
} from '../../core/queries';
import { sortedIds } from '../../core/sorted';
import type {
  CareOutcome,
  HealthCareOption,
  IllnessId,
  Person,
  PersonId,
  PhaseContext,
  ReadContext,
  TrustCause,
} from '../../core/types';
import { assertNever } from '../../core/types';
import { remember } from '../shared/memory';
import { addStock } from '../shared/stocks';
import { num, valueOf } from '../shared/value';
import { infect } from './infect';

const EPS = 1e-9;

/** Healing capacity by source: healers in id order, then slots from anything else (null). */
export interface CareSource {
  id: PersonId | null;
  slots: number;
}

export interface CareInputs {
  /** The season being planned (patients caught in this season wait until the next). */
  turn: number;
  sources: readonly CareSource[];
  medicine: number;
  /** Medicine per patient for a full dose (the `demand` target for medicine). */
  doses: Readonly<Partial<Record<PersonId, number>>>;
}

export interface CarePlan {
  slots: number;
  /** Whole patients the healers can see this season. */
  capacity: number;
  /** Patients in triage order: player-marked first, then the policy's order; excluded last. */
  ranked: PersonId[];
  /** Patients the player marked "do not treat". */
  excluded: PersonId[];
  treated: PersonId[];
  care: Record<PersonId, CareOutcome>;
  healerOf: Record<PersonId, PersonId | null>;
  /** Untreated death chance per patient this season (the triage key). */
  deathChance: Record<PersonId, number>;
  medicineAvailable: number;
  medicineUsed: number;
  /** Doses every patient would need. */
  medicineDemand: number;
  shortOfMedicine: boolean;
  visits: PersonId[];
}

/** Chance of dying this season if untreated: sickness and injury combined. */
export function untreatedDeathChance(ctx: ReadContext, p: Person): number {
  let survive = 1;
  if (p.status.sick !== null) {
    survive *=
      1 - num(ctx, { kind: 'illnessDeath', personId: p.id, illnessId: p.status.sick.illnessId });
  }
  if (p.status.injured !== null) {
    survive *= 1 - num(ctx, { kind: 'illnessDeath', personId: p.id, illnessId: 'injury' });
  }
  return clamp(1 - survive, 0, 1);
}

/** Section 18 health-care policy: which patients come first (lower tier first). */
function tier(ctx: ReadContext, policy: HealthCareOption, p: Person): number {
  switch (policy) {
    case 'most-likely-to-die':
    case 'manual':
      return 0;
    case 'contagious-first':
      return p.status.sick === null ? 0 : -ctx.cfg.illnesses[p.status.sick.illnessId].contagion;
    case 'workers-first':
      return p.job === 'rest' ? 1 : 0;
    case 'children-first':
      return isChild(ctx.cfg, p) ? 0 : 1;
    default:
      return assertNever(policy, 'health-care policy');
  }
}

/**
 * Section 12 triage. The player's marks come first ("override by name"), then the policy's
 * order; ties go to the most likely to die, then the lowest health, then id. Under "manual" the
 * proposal is most-likely-to-die, like the Manual ration policy (C-05).
 */
export function planCare(ctx: ReadContext, inputs: CareInputs): CarePlan {
  const { state, cfg } = ctx;
  const policy = state.policies.healthCare;
  const patients = peopleSorted(state).filter((p) => isPatient(p, inputs.turn));
  const deathChance: Record<PersonId, number> = {};
  for (const p of patients) deathChance[p.id] = untreatedDeathChance(ctx, p);
  const mark = (p: Person): number =>
    state.triageOverrides[p.id] === true ? 0 : state.triageOverrides[p.id] === false ? 2 : 1;
  const ordered = patients
    .slice()
    .sort(
      (a, b) =>
        mark(a) - mark(b) ||
        tier(ctx, policy, a) - tier(ctx, policy, b) ||
        (deathChance[b.id] ?? 0) - (deathChance[a.id] ?? 0) ||
        a.health - b.health ||
        (a.id < b.id ? -1 : 1),
    );
  const ranked = ordered.map((p) => p.id);
  const excluded = ordered.filter((p) => mark(p) === 2).map((p) => p.id);
  const eligible = ordered.filter((p) => mark(p) !== 2).map((p) => p.id);

  let slots = 0;
  for (const s of inputs.sources) slots += Math.max(0, s.slots);
  const capacity = Math.floor(slots + EPS);
  const treated = eligible.slice(0, capacity);

  const care: Record<PersonId, CareOutcome> = {};
  let medicineDemand = 0;
  for (const id of ranked) medicineDemand += inputs.doses[id] ?? 0;
  let left = Math.max(0, inputs.medicine);
  let used = 0;
  for (const id of treated) {
    const dose = inputs.doses[id] ?? 0;
    if (dose <= left + EPS) {
      care[id] = 'medicine';
      const take = Math.min(dose, left);
      left -= take;
      used += take;
    } else {
      care[id] = 'no-medicine';
    }
  }
  for (const id of ranked) care[id] ??= capacity > 0 ? 'passed-over' : 'untreated';

  // Which healer saw whom: cumulative slots in source order.
  const healerOf: Record<PersonId, PersonId | null> = {};
  let source = 0;
  let covered = inputs.sources[0]?.slots ?? 0;
  treated.forEach((id, k) => {
    while (k + EPS >= covered && source < inputs.sources.length - 1) {
      source++;
      covered += inputs.sources[source]?.slots ?? 0;
    }
    healerOf[id] = inputs.sources[source]?.id ?? null;
  });

  // Section 12 recovery: spare capacity visits the healthy (all needs met), lowest health first.
  const spare = capacity - treated.length;
  const visits: PersonId[] = [];
  if (spare > 0) {
    const met = cfg.needs.metThreshold;
    const candidates = peopleSorted(state).filter((p) => {
      if (p.status.sick !== null || p.status.injured !== null) return false;
      if (p.health >= cfg.people.statMax - EPS) return false;
      const n = needsOf(state, p.id);
      return n.food >= met && n.water >= met && n.shelter >= met && n.warmth >= met;
    });
    candidates.sort((a, b) => a.health - b.health || (a.id < b.id ? -1 : 1));
    for (const p of candidates.slice(0, spare)) {
      visits.push(p.id);
      care[p.id] = 'visit';
    }
  }

  return {
    slots,
    capacity,
    ranked,
    excluded,
    treated,
    care,
    healerOf,
    deathChance,
    medicineAvailable: Math.max(0, inputs.medicine),
    medicineUsed: used,
    medicineDemand,
    shortOfMedicine: inputs.medicine + EPS < medicineDemand,
    visits,
  };
}

/** The inputs for the season in progress: this season's healing slots, medicine and doses. */
function seasonInputs(ctx: ReadContext): CareInputs {
  const report = ctx.state.currentReport;
  const sources: CareSource[] = [];
  let fromHealers = 0;
  if (report !== null) {
    for (const id of sortedIds(report.outputs)) {
      const out = report.outputs[id];
      if (out?.jobId !== 'healer' || out.amount <= 0) continue;
      sources.push({ id, slots: out.amount });
      fromHealers += out.amount;
    }
    const other = (report.services.healingSlots ?? 0) - fromHealers;
    if (other > EPS) sources.push({ id: null, slots: other });
  }
  return {
    turn: ctx.season.turn,
    sources,
    medicine: ctx.state.stocks.medicine.amount,
    doses: report?.demand.medicine ?? {},
  };
}

/** This season's plan, memoised on the phase-start state. */
export function carePlan(ctx: ReadContext): CarePlan {
  return memoByState(ctx.state, 'care:plan', () => planCare(ctx, seasonInputs(ctx)));
}

/** A read context whose report carries this season's care (see the header). */
export function careContext(ctx: ReadContext): ReadContext {
  return memoByState(ctx.state, 'care:context', () => {
    const report = ctx.state.currentReport;
    const read: ReadContext = {
      state: ctx.state,
      cfg: ctx.cfg,
      registry: ctx.registry,
      season: ctx.season,
      mode: ctx.mode,
    };
    if (report === null) return read;
    return {
      ...read,
      state: { ...ctx.state, currentReport: { ...report, care: carePlan(ctx).care } },
    };
  });
}

interface Tally {
  delta: number;
  people: number;
}

function changeTrust(
  ctx: PhaseContext,
  read: ReadContext,
  p: Person,
  cause: TrustCause,
  tally: Map<TrustCause, Tally>,
): void {
  const delta = num(read, { kind: 'trustChange', personId: p.id, cause });
  if (delta === 0) return;
  p.trust = clamp(p.trust + delta, 0, ctx.cfg.people.statMax);
  const row = tally.get(cause) ?? { delta: 0, people: 0 };
  row.delta += delta;
  row.people += 1;
  tally.set(cause, row);
}

/** A full treatment switches the illness and injury to their treated durations (Section 12). */
function shorten(ctx: PhaseContext, p: Person): void {
  const turn = ctx.season.turn;
  const sick = p.status.sick;
  if (sick !== null && sick.sinceTurn !== turn) {
    const def = ctx.registry.illnesses[sick.illnessId];
    sick.treated = true;
    sick.seasonsRemaining = Math.min(
      sick.seasonsRemaining,
      Math.max(1, def.treatedSeasons - sick.seasonsSick),
    );
  }
  const hurt = p.status.injured;
  if (hurt !== null && hurt.sinceTurn !== turn) {
    const def = ctx.registry.illnesses.injury;
    const elapsed = def.untreatedSeasons - hurt.seasonsRemaining;
    hurt.treated = true;
    hurt.seasonsRemaining = Math.min(
      hurt.seasonsRemaining,
      Math.max(1, def.treatedSeasons - elapsed),
    );
  }
}

/** The most contagious illness among a healer's patients (plague first), if any. */
function catchable(ctx: PhaseContext, patients: readonly PersonId[]): IllnessId | null {
  let best: IllnessId | null = null;
  let weight = 0;
  for (const id of patients) {
    const sick = ctx.state.people[id]?.status.sick;
    if (sick === null || sick === undefined) continue;
    const w = ctx.cfg.illnesses[sick.illnessId].contagion;
    if (w > weight) {
      weight = w;
      best = sick.illnessId;
    }
  }
  return best;
}

/** Section 12: healers catch what they treat (3%, plague 10%), one roll per healer per season. */
function healersCatch(ctx: PhaseContext, plan: CarePlan, read: ReadContext): void {
  const byHealer = new Map<PersonId, PersonId[]>();
  for (const id of plan.treated) {
    const healer = plan.healerOf[id];
    if (healer === null || healer === undefined) continue;
    const list = byHealer.get(healer) ?? [];
    list.push(id);
    byHealer.set(healer, list);
  }
  for (const healerId of [...byHealer.keys()].sort()) {
    const illnessId = catchable(ctx, byHealer.get(healerId) ?? []);
    if (illnessId === null || ctx.draft.people[healerId] === undefined) continue;
    const roll = ctx.rng.chance(num(read, { kind: 'catchRisk', personId: healerId, illnessId }));
    if (ctx.mode === 'projection') {
      const report = ctx.report.current;
      report.expectedIllnesses = (report.expectedIllnesses ?? 0) + roll;
    }
    if (ctx.rng.happened(roll)) infect(ctx, healerId, illnessId, { source: 'job:healer' });
  }
}

/** Triage results: medicine, durations, entries, memory, trust, the health-care need. */
function applyCare(ctx: PhaseContext): void {
  const plan = carePlan(ctx);
  const read = careContext(ctx);
  const report = ctx.report.current;
  const turn = ctx.season.turn;
  if (Object.keys(plan.care).length > 0) report.care = { ...plan.care };
  if (plan.medicineUsed > 0) addStock(ctx, 'medicine', -plan.medicineUsed, 'consumption');
  if (plan.ranked.length > 0 || plan.medicineUsed > 0) {
    report.distribution.medicine = {
      available: plan.medicineAvailable,
      distributed: plan.medicineUsed,
    };
  }
  const tally = new Map<TrustCause, Tally>();
  for (const id of plan.treated) {
    const p = ctx.draft.people[id];
    if (p === undefined) continue;
    const withMedicine = plan.care[id] === 'medicine';
    if (withMedicine) shorten(ctx, p);
    ctx.report.add({
      kind: 'treatment',
      healerId: plan.healerOf[id] ?? null,
      patientId: id,
      withMedicine,
    });
    remember(ctx, p, { turn, kind: 'saved-by-healer', amount: withMedicine ? 1 : 0.5 });
    if (withMedicine && plan.shortOfMedicine)
      changeTrust(ctx, read, p, 'treated-short-medicine', tally);
    const needs = report.needs[id];
    if (needs !== undefined)
      needs.healthCare = withMedicine ? 1 : ctx.cfg.illness.healthCareWithoutMedicine;
  }
  for (const id of plan.ranked) {
    if (plan.care[id] !== 'passed-over') continue;
    const p = ctx.draft.people[id];
    if (p === undefined) continue;
    ctx.report.add({ kind: 'passed-over', personId: id });
    remember(ctx, p, { turn, kind: 'passed-over' });
    changeTrust(ctx, read, p, 'passed-over-treatment', tally);
  }
  for (const [cause, row] of [...tally.entries()].sort(([a], [b]) => (a < b ? -1 : 1))) {
    ctx.report.add({ kind: 'trust', cause, delta: row.delta / row.people, people: row.people });
  }
  healersCatch(ctx, plan, read);
}

/** Section 19: expire timers; charge isolation food for the people held this season. */
function upkeepQuarantine(ctx: PhaseContext): void {
  const q = ctx.draft.quarantine;
  const turn = ctx.season.turn;
  if (q.contagionBoostUntilTurn !== null && q.contagionBoostUntilTurn < turn)
    q.contagionBoostUntilTurn = null;
  if (q.active && q.untilTurn !== null && q.untilTurn < turn) {
    q.active = false;
    q.untilTurn = null;
    ctx.report.add({ kind: 'quarantine', change: 'lifted', people: 0, food: 0 });
  }
  if (!quarantineActive(ctx.state, turn)) return;
  let held = 0;
  for (const p of peopleSorted(ctx.state)) if (isIsolated(ctx.cfg, ctx.state, p, turn)) held++;
  if (held === 0) return;
  const cost = held * ctx.cfg.events['fever-outbreak'].quarantineFoodPerSick;
  const paid = -addStock(ctx, 'food', -cost, 'other');
  ctx.report.add({ kind: 'quarantine', change: 'held', people: held, food: paid });
}

/** Start of the bodies phase: quarantine upkeep, then triage and treatment. */
export function beginHealth(ctx: PhaseContext): void {
  upkeepQuarantine(ctx);
  applyCare(ctx);
}

/**
 * End of the bodies phase: record the illness multiplier as rolled, keep quarantine flags in
 * step with who is sick, lift an open-ended quarantine once no one is contagious, and drop
 * triage marks for people who are no longer sick or injured.
 */
export function endHealth(ctx: PhaseContext): void {
  const report = ctx.report.current;
  const evaluation = valueOf(ctx, { kind: 'illnessMultiplier' });
  report.illnessMultiplier = evaluation.value;
  report.illnessFactors = evaluation.factors;

  const turn = ctx.season.turn;
  const q = ctx.draft.quarantine;
  let active = q.active && (q.untilTurn === null || q.untilTurn >= turn);
  if (active && q.untilTurn === null && contagiousCount(ctx.draft, ctx.cfg) === 0) {
    q.active = false;
    active = false;
    ctx.report.add({ kind: 'quarantine', change: 'lifted', people: 0, food: 0 });
  }
  if (active) {
    for (const p of peopleSorted(ctx.draft)) {
      const sick = p.status.sick;
      const isolate = sick !== null && ctx.cfg.illnesses[sick.illnessId].contagion > 0;
      if (p.status.quarantined !== isolate) p.status.quarantined = isolate;
    }
  } else {
    for (const p of peopleSorted(ctx.state)) {
      const target = p.status.quarantined ? ctx.draft.people[p.id] : undefined;
      if (target !== undefined) target.status.quarantined = false;
    }
  }

  for (const id of sortedIds(ctx.draft.triageOverrides)) {
    const p = ctx.draft.people[id];
    if (p === undefined || (p.status.sick === null && p.status.injured === null))
      delete ctx.draft.triageOverrides[id];
  }
}
